import { create } from "zustand";
import { persist } from "zustand/middleware";
import { toast } from "sonner";
import { loginUser } from "../api/userApi";
import type { UserInfo } from "../api/userApi";

interface AuthState {
  userInfo: UserInfo | null;
  isLoading: boolean;
  error: string | null;

  // Actions
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  clearError: () => void;
  updateTokens: (token: string, refreshToken: string) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      userInfo: null,
      isLoading: false,
      error: null,

      login: async (email, password) => {
        set({ isLoading: true, error: null });
        try {
          const userInfo = await loginUser({ email, password });
          set({ userInfo, isLoading: false });
          return true;
        } catch (error: unknown) {
          const message =
            error instanceof Error
              ? (error as { response?: { data?: { message?: string } } }).response?.data?.message ||
                error.message
              : "Login failed";
          set({ error: message, isLoading: false });
          toast.error("Login failed", { description: message });
          return false;
        }
      },

      logout: () => {
        set({ userInfo: null, error: null });
      },

      clearError: () => {
        set({ error: null });
      },

      updateTokens: (token, refreshToken) => {
        const { userInfo } = get();
        if (userInfo) {
          set({ userInfo: { ...userInfo, token, refreshToken } });
        }
      },
    }),
    {
      name: "userInfo",
      partialize: (state) => ({ userInfo: state.userInfo }),
    }
  )
);
